<template>
  <q-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    @hide="reset"
  >
    <q-card class="support-video-dialog">
      <q-card-section class="support-video-header">
        <h2 class="support-video-title">{{ title }}</h2>
        <q-btn
          flat
          round
          dense
          icon="close"
          aria-label="Close video"
          @click="$emit('update:modelValue', false)"
        >
          <q-tooltip>Close video</q-tooltip>
        </q-btn>
      </q-card-section>
      <q-card-section class="q-pt-none">
        <div class="support-video-frame" :aria-busy="loading">
          <video
            v-if="videoUrl && getPlayback"
            ref="player"
            :src="videoUrl"
            :aria-label="title"
            controls
            controlslist="nodownload"
            playsinline
            preload="metadata"
            @timeupdate="trackPosition"
            @error="onMediaError"
          ></video>
          <iframe
            v-else-if="embedUrl && modelValue"
            :src="embedUrl"
            :title="title"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
          ></iframe>
          <q-spinner
            v-if="loading"
            class="support-video-spinner"
            color="primary"
            size="40px"
            aria-label="Loading video"
          />
        </div>
        <div v-if="error" class="support-video-error" role="alert">
          <span>{{ error }}</span>
          <q-btn
            v-if="canRetry"
            flat
            no-caps
            color="primary"
            icon="refresh"
            label="Reload"
            :loading="loading"
            @click="loadPlayback(true)"
          />
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import type { SupportVideoPlayback } from '../services/SupportCitationParser';
import { getYouTubeEmbedUrl } from '../services/SupportMediaPolicy';
import {
  SUPPORT_VIDEO_INTERRUPTED_MESSAGE,
  describeSupportVideoFailure,
  shouldRenewSupportVideo,
  trackSupportVideoPosition,
} from '../services/SupportVideoPlaybackPolicy';

export default defineComponent({
  name: 'SupportVideoPlayer',
  props: {
    modelValue: { type: Boolean, required: true },
    title: { type: String, default: '' },
    sourceUrl: { type: String, default: '' },
    getPlayback: {
      type: Function as unknown as PropType<
        (() => Promise<SupportVideoPlayback>) | null
      >,
      default: null,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      videoUrl: '',
      error: '',
      loading: false,
      canRetry: true,
      position: 0,
      renewedAt: 0,
      operation: 0,
      pendingResume: null as {
        player: HTMLVideoElement;
        listener: () => void;
      } | null,
    };
  },
  computed: {
    embedUrl(): string | null {
      return getYouTubeEmbedUrl(this.sourceUrl);
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open: boolean) {
        if (!open) this.reset();
        else if (this.getPlayback) void this.loadPlayback();
      },
    },
  },
  beforeUnmount() {
    this.reset();
  },
  methods: {
    async loadPlayback(renewal = false) {
      if (!this.getPlayback || !this.modelValue || this.loading) return;
      const operation = ++this.operation;
      const resumeAt = this.position;
      this.loading = true;
      if (renewal) this.renewedAt = Date.now();
      try {
        const playback = await this.getPlayback();
        if (operation !== this.operation || !this.modelValue) return;
        if (!playback.url.startsWith('https://'))
          throw new Error('Invalid support playback address.');
        const player = this.$refs.player as HTMLVideoElement | undefined;
        this.clearResume();
        if (renewal && player && resumeAt > 0) {
          const listener = () => {
            this.pendingResume = null;
            if (operation !== this.operation || !this.modelValue) return;
            player.currentTime = Number.isFinite(player.duration)
              ? Math.min(resumeAt, player.duration)
              : resumeAt;
            void player.play().catch(() => undefined);
          };
          this.pendingResume = { player, listener };
          player.addEventListener('loadedmetadata', listener, { once: true });
        }
        this.videoUrl = playback.url;
        this.error = '';
        this.canRetry = true;
      } catch (error) {
        if (operation !== this.operation || !this.modelValue) return;
        const failure = describeSupportVideoFailure(error);
        this.error =
          renewal && failure.status !== 404
            ? SUPPORT_VIDEO_INTERRUPTED_MESSAGE
            : failure.message;
        this.canRetry = failure.canRetry;
        if (!failure.canRetry) this.videoUrl = '';
      } finally {
        if (operation === this.operation) this.loading = false;
      }
    },
    trackPosition(event: Event) {
      const player = event.target as HTMLVideoElement;
      this.position = trackSupportVideoPosition(
        this.position,
        player.currentTime
      );
    },
    async onMediaError() {
      if (!this.getPlayback || !this.videoUrl || this.loading) return;
      if (!shouldRenewSupportVideo(this.renewedAt, Date.now())) {
        this.error = SUPPORT_VIDEO_INTERRUPTED_MESSAGE;
        this.canRetry = true;
        return;
      }
      await this.loadPlayback(true);
    },
    clearResume() {
      if (this.pendingResume) {
        this.pendingResume.player.removeEventListener(
          'loadedmetadata',
          this.pendingResume.listener
        );
        this.pendingResume = null;
      }
    },
    reset() {
      this.operation++;
      this.clearResume();
      this.videoUrl = '';
      this.error = '';
      this.loading = false;
      this.canRetry = true;
      this.position = 0;
      this.renewedAt = 0;
    },
  },
});
</script>

<style scoped>
.support-video-dialog {
  width: min(960px, 96vw);
  max-width: 96vw;
  border-radius: 8px;
}
.support-video-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.support-video-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 18px;
  line-height: 1.4;
  font-weight: 600;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.support-video-frame {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #111;
}
.support-video-frame video,
.support-video-frame iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}
.support-video-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.support-video-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  color: #a71930;
  overflow-wrap: anywhere;
}
</style>
